import { useEffect, useRef, useState } from "react";
import { useApiFetch } from "../api/client";
import { Input } from "./ui/Input";

export interface ComboClient {
  clientId: number;
  clientName: string;
  clientCode: string | null;
  status: string;
}

interface Props {
  id: string;
  selected: ComboClient | null;
  onSelect: (client: ComboClient) => void;
  onQueryChange?: (query: string) => void;
  onMatchesChange?: (matches: ComboClient[]) => void;
  placeholder?: string;
}

// A searchable dropdown: click or focus opens the list immediately (an
// empty query returns the first page of clients), typing filters it via
// the same /clients?q= endpoint, arrow keys move the highlight, Enter
// selects. Selection display and search input share one field, like a
// native <select> that can also be typed into.
export function ClientCombobox({ id, selected, onSelect, onQueryChange, onMatchesChange, placeholder }: Props) {
  const apiFetch = useApiFetch();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<ComboClient[]>([]);
  const [highlighted, setHighlighted] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      try {
        const res = await apiFetch(`clients?q=${encodeURIComponent(query)}`);
        const found = await res.json();
        setMatches(found);
        onMatchesChange?.(found);
        setHighlighted(0);
      } catch {
        setMatches([]);
        onMatchesChange?.([]);
      }
    }, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const select = (c: ComboClient) => {
    onSelect(c);
    setOpen(false);
    setQuery("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (matches[highlighted]) select(matches[highlighted]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={rootRef}>
      <div className="relative">
        <Input
          id={id}
          className="pr-[34px] cursor-text"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          autoComplete="off"
          placeholder={placeholder ?? "Search for a client by name…"}
          value={open ? query : (selected?.clientName ?? "")}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            onQueryChange?.(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          className="absolute right-1 top-1/2 -translate-y-1/2 bg-transparent border-none text-rs-light-gray p-1.5 hover:text-rs-green"
          aria-label={open ? "Close client list" : "Open client list"}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="material-symbols-sharp">{open ? "expand_less" : "expand_more"}</span>
        </button>
      </div>
      {open && (
        <div
          className="absolute top-[calc(100%+6px)] left-0 right-0 z-30 max-h-[280px] overflow-y-auto bg-rs-jet border border-rs-jet rounded-lg"
          id={`${id}-listbox`}
          role="listbox"
        >
          {matches.length === 0 && (
            <div className="py-[13px] px-4 text-rs-light-gray text-[13px]">No clients match.</div>
          )}
          {matches.map((c, i) => (
            <button
              key={c.clientId}
              type="button"
              role="option"
              aria-selected={i === highlighted}
              className={`block w-full text-left bg-transparent border-0 border-b border-l-2 border-rs-jet py-[13px] px-4 text-rs-off-white hover:bg-rs-off-black ${
                i === highlighted ? "bg-rs-off-black border-l-rs-green" : "border-l-transparent"
              }`}
              onMouseEnter={() => setHighlighted(i)}
              onClick={() => select(c)}
            >
              {c.clientName}{c.clientCode ? ` (${c.clientCode})` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
